import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  checkBrandingDomain,
  deleteDomain,
  putLinkDomain,
  putSendingDomain,
  type DomainDnsRecord,
  type DomainStatus,
  type LinkDomain,
  type SendingDomain,
} from "@/api/branding";
import { t, type MessageKey } from "@/i18n";

/**
 * The lab's own sending domain and patient link host, with where each stands (B9, D11).
 *
 * Neither is inherited (D19): a branch with none of its own sends from ours and links on
 * ours. Until a domain verifies nothing waits (D15): emails go from our address and links
 * use our host, and both switch on their own once it verifies. The status here is the
 * server's, set by its checker, never by this page. "Check now" asks the server to run that
 * checker at once rather than wait for its next round, and shows per record whether DNS
 * holds it.
 */

const STATUS_COLOR: Record<DomainStatus, string> = {
  pending: "yellow",
  verified: "green",
  failed: "red",
};

const STATUS_TEXT: Record<DomainStatus, MessageKey> = {
  pending: "branding.domain_pending",
  verified: "branding.domain_verified",
  failed: "branding.domain_failed",
};

const PURPOSE_TEXT: Record<string, MessageKey> = {
  dkim: "branding.dns_purpose_dkim",
  mail_from_mx: "branding.dns_purpose_mail_from_mx",
  mail_from_spf: "branding.dns_purpose_mail_from_spf",
  link_cname: "branding.dns_purpose_link_cname",
};

function foundText(found: boolean | null): string {
  if (found === null) return t("branding.dns_not_checked");
  return found ? t("branding.dns_found") : t("branding.dns_missing");
}

/** Every record the lab adds, MAIL FROM included, with whether a check found it. */
export function DnsRecordsTable({
  records,
  testId,
}: {
  records: DomainDnsRecord[];
  testId: string;
}): JSX.Element {
  return (
    <Table withTableBorder data-testid={testId}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("branding.dns_purpose")}</Table.Th>
          <Table.Th>{t("branding.dns_type")}</Table.Th>
          <Table.Th>{t("branding.dns_name")}</Table.Th>
          <Table.Th>{t("branding.dns_value")}</Table.Th>
          <Table.Th>{t("branding.dns_required")}</Table.Th>
          <Table.Th>{t("branding.dns_found_column")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {records.map((r) => {
          const purpose = PURPOSE_TEXT[r.purpose];
          return (
            <Table.Tr key={`${r.purpose}:${r.type}:${r.name}:${r.value}`} data-testid="dns-record">
              <Table.Td>{purpose !== undefined ? t(purpose) : r.purpose}</Table.Td>
              <Table.Td>{r.type}</Table.Td>
              <Table.Td>
                <Code>{r.name}</Code>
              </Table.Td>
              <Table.Td>
                <Code>{r.value}</Code>
              </Table.Td>
              <Table.Td>
                {r.required ? t("branding.dns_required_yes") : t("branding.dns_required_no")}
              </Table.Td>
              <Table.Td data-testid="dns-record-found">
                <Badge
                  variant="light"
                  color={r.found === null ? "gray" : r.found ? "green" : "red"}
                >
                  {foundText(r.found)}
                </Badge>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

export function DomainStatusBadge({ status }: { status: DomainStatus }): JSX.Element {
  return (
    <Badge color={STATUS_COLOR[status]} variant="light" data-testid="domain-status">
      {t(STATUS_TEXT[status])}
    </Badge>
  );
}

interface DomainsCardProps {
  organizationId: string;
  sending: SendingDomain | null;
  link: LinkDomain | null;
}

export function DomainsCard({ organizationId, sending, link }: DomainsCardProps): JSX.Element {
  return (
    <Card withBorder data-testid="branding-domains">
      <Title order={3} mb="xs">
        {t("branding.domains_title")}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t("branding.domains_hint")}
      </Text>
      <Stack gap="lg">
        <DomainRow
          organizationId={organizationId}
          kind="sending"
          label={t("branding.sending_domain")}
          placeholder="lab.example"
          current={sending?.domain ?? null}
          status={sending?.status ?? null}
          failureReason={sending?.failure_reason ?? null}
          records={sending?.records ?? null}
        >
          {sending !== null && sending.records === undefined && sending.dkim_records.length > 0 && (
            <Table withTableBorder data-testid="dkim-records">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("branding.dns_name")}</Table.Th>
                  <Table.Th>{t("branding.dns_type")}</Table.Th>
                  <Table.Th>{t("branding.dns_value")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sending.dkim_records.map((r) => (
                  <Table.Tr key={r.name}>
                    <Table.Td>
                      <Code>{r.name}</Code>
                    </Table.Td>
                    <Table.Td>{r.type}</Table.Td>
                    <Table.Td>
                      <Code>{r.value}</Code>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          {sending?.mail_from_domain && (
            <Text size="sm">
              {t("branding.mail_from")} <Code>{sending.mail_from_domain}</Code>
            </Text>
          )}
        </DomainRow>
        <DomainRow
          organizationId={organizationId}
          kind="link"
          label={t("branding.link_domain")}
          placeholder="reports.lab.example"
          current={link?.host ?? null}
          status={link?.status ?? null}
          failureReason={link?.failure_reason ?? null}
          records={link?.records ?? null}
        >
          {link !== null && (
            <Text size="sm" data-testid="link-cname">
              {t("branding.cname_instruction")} <Code>{link.host}</Code> {t("branding.cname_to")}{" "}
              <Code>{link.cname_target}</Code>
            </Text>
          )}
        </DomainRow>
      </Stack>
    </Card>
  );
}

interface DomainRowProps {
  organizationId: string;
  kind: "sending" | "link";
  label: string;
  placeholder: string;
  current: string | null;
  status: DomainStatus | null;
  failureReason: string | null;
  /** The records as the read lists them, `found` null. Null with no domain or an older server. */
  records: DomainDnsRecord[] | null;
  children?: React.ReactNode;
}

function DomainRow({
  organizationId,
  kind,
  label,
  placeholder,
  current,
  status,
  failureReason,
  records,
  children,
}: DomainRowProps): JSX.Element {
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  // The answer to the last check, kept so its `found` ticks survive the refresh of the
  // record, whose own read carries none. Shown only while it is for the domain on file.
  const [checked, setChecked] = useState<{
    name: string;
    records: DomainDnsRecord[] | null;
  } | null>(null);
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["branding", organizationId] });
  const save = useMutation({
    mutationFn: async (v: string): Promise<void> => {
      if (kind === "sending") await putSendingDomain(organizationId, v);
      else await putLinkDomain(organizationId, v);
    },
    onSuccess: async () => {
      setValue("");
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteDomain(organizationId, kind),
    onSuccess: async () => {
      setChecked(null);
      await refresh();
    },
  });
  const check = useMutation({
    mutationFn: () => checkBrandingDomain(organizationId, kind),
    onSuccess: async (answer) => {
      setChecked({
        name: answer.kind === "sending" ? answer.domain : answer.host,
        records: answer.records ?? null,
      });
      await refresh();
    },
  });
  const shown =
    checked !== null && checked.name === current && checked.records !== null
      ? checked.records
      : records;

  return (
    <Stack gap="xs" data-testid={`domain-${kind}`}>
      <Group gap="sm">
        <Text fw={600} size="sm">
          {label}
        </Text>
        {current !== null && <Code>{current}</Code>}
        {status !== null && <DomainStatusBadge status={status} />}
        {current === null && (
          <Text size="sm" c="dimmed">
            {t("branding.domain_none")}
          </Text>
        )}
      </Group>
      {failureReason !== null && (
        <Text size="sm" c="dimmed" data-testid={`domain-${kind}-reason`}>
          {t("branding.domain_reason")} <Code>{failureReason}</Code>
        </Text>
      )}
      {shown !== null && shown.length > 0 && (
        <DnsRecordsTable records={shown} testId={`domain-${kind}-records`} />
      )}
      {children}
      <Group align="flex-end">
        <TextInput
          label={current === null ? t("branding.domain_set") : t("branding.domain_change")}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value.trim().toLowerCase())}
          w={300}
          data-testid={`domain-${kind}-input`}
        />
        <Button
          variant="light"
          disabled={value === ""}
          loading={save.isPending}
          onClick={() => save.mutate(value)}
          data-testid={`domain-${kind}-save`}
        >
          {t("branding.domain_save")}
        </Button>
        {current !== null && (
          <Button
            variant="light"
            loading={check.isPending}
            onClick={() => check.mutate()}
            data-testid={`domain-${kind}-check`}
          >
            {t("branding.domain_check")}
          </Button>
        )}
        {current !== null && (
          <Button
            variant="subtle"
            color="gray"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            data-testid={`domain-${kind}-remove`}
          >
            {t("branding.domain_remove")}
          </Button>
        )}
      </Group>
      {(save.isError || remove.isError || check.isError) && (
        <Alert color="red" p="xs" data-testid={`domain-${kind}-error`}>
          {((save.error ?? remove.error ?? check.error) as Error).message}
        </Alert>
      )}
    </Stack>
  );
}
